import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import TitleCard from '@/components/feature/TitleCard';
import Pagination from '@/components/feature/Pagination';
import {
  filterTitles,
  getGenres,
  getRegions,
  getYears,
  paginate,
  PAGE_SIZE,
  SORT_OPTIONS,
} from '@/lib/catalog';

interface CatalogPageProps {
  type: 'movie' | 'tv';
  title: string;
  description: string;
}

interface Option {
  value: string;
  label: string;
}

function buildOptions(values: string[]): Option[] {
  return [{ value: 'all', label: '全部' }, ...values.map((v) => ({ value: v, label: v }))];
}

function FilterGroup({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Option[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2 border-b border-background-200 py-3.5 last:border-b-0 sm:flex-row sm:items-start sm:gap-4">
      <span className="shrink-0 pt-1 text-sm font-semibold text-foreground-700 sm:w-16">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
              value === opt.value
                ? 'bg-primary-600 text-background-50'
                : 'bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-900'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function CatalogPage({ type, title, description }: CatalogPageProps) {
  const [params] = useSearchParams();
  const [genre, setGenre] = useState(params.get('genre') || 'all');
  const [year, setYear] = useState(params.get('year') || 'all');
  const [region, setRegion] = useState(params.get('region') || 'all');
  const [sort, setSort] = useState(params.get('sort') || 'latest');
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const genreOptions = useMemo(() => buildOptions(getGenres(type)), [type]);
  const yearOptions = useMemo(() => buildOptions(getYears(type)), [type]);
  const regionOptions = useMemo(() => buildOptions(getRegions(type)), [type]);

  const filtered = useMemo(
    () => filterTitles({ type, genre, year, region, sort }),
    [type, genre, year, region, sort],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = paginate(filtered, Math.min(page, totalPages));

  const update = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setPage(1);
  };

  const resetFilters = () => {
    setGenre('all');
    setYear('all');
    setRegion('all');
    setSort('latest');
    setPage(1);
  };

  const label = type === 'tv' ? '电视剧' : '电影';
  const activeCount = [genre, year, region].filter((v) => v !== 'all').length;

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16 md:pt-[72px]">
        {/* header */}
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
            <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="font-medium text-foreground-800">{label}分类</span>
            </nav>
            <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
              {title}
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">{description}</p>
          </div>
        </div>

        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
          <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
            {/* Filters */}
            <aside className="lg:w-72 lg:shrink-0">
              <div className="rounded-xl border border-background-200 bg-background-50 p-4 md:p-5 lg:sticky lg:top-24">
                <div className="flex items-center justify-between lg:mb-2">
                  <h2 className="flex items-center gap-2 text-sm font-bold text-foreground-900">
                    <i className="ri-filter-3-line text-base text-primary-600" />
                    筛选条件
                    {activeCount > 0 && (
                      <span className="rounded-full bg-accent-500 px-1.5 text-[11px] font-bold text-primary-950">
                        {activeCount}
                      </span>
                    )}
                  </h2>
                  <button
                    type="button"
                    onClick={() => setFilterOpen((v) => !v)}
                    className="flex items-center gap-1 text-xs text-foreground-600 lg:hidden"
                  >
                    {filterOpen ? '收起' : '展开'}
                    <i className={`${filterOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-base`} />
                  </button>
                </div>

                <div className={`${filterOpen ? 'block' : 'hidden'} lg:block`}>
                  <FilterGroup label="类型" options={genreOptions} value={genre} onChange={update(setGenre)} />
                  <FilterGroup label="年份" options={yearOptions} value={year} onChange={update(setYear)} />
                  <FilterGroup label="地区" options={regionOptions} value={region} onChange={update(setRegion)} />
                  <FilterGroup
                    label="排序"
                    options={SORT_OPTIONS}
                    value={sort}
                    onChange={update(setSort)}
                  />
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="mt-4 w-full whitespace-nowrap rounded-md border border-background-300 bg-background-100 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    重置筛选
                  </button>
                </div>
              </div>
            </aside>

            {/* Results */}
            <div className="min-w-0 flex-1">
              <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-foreground-600">
                  共找到 <span className="font-bold text-primary-600">{filtered.length}</span> 部{label}
                </p>
                <p className="text-xs text-foreground-500">
                  第 {Math.min(page, totalPages)} / {totalPages} 页
                </p>
              </div>

              {pageItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 bg-background-100 py-20 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                    <i className="ri-inbox-archive-line text-3xl" />
                  </span>
                  <p className="mt-4 text-base font-medium text-foreground-700">没有找到匹配的影视资源</p>
                  <p className="mt-1 text-sm text-foreground-500">试试调整或重置筛选条件吧</p>
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="mt-5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
                  >
                    重置筛选
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 xl:grid-cols-4">
                  {pageItems.map((item) => (
                    <TitleCard key={item.id} item={item} />
                  ))}
                </div>
              )}

              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}